/**
 * Hayanoon — scroll reveal.
 *
 * Adds a light entrance to each section as it scrolls into view.
 *
 * Why JavaScript at all: the CSS-only approach (`animation-timeline: view()`)
 * is Chromium-only today, so Safari and Firefox visitors would see nothing.
 * This is ~1 KB, uses IntersectionObserver, unobserves each element once it has
 * fired, and never runs a scroll handler — so there is no per-frame cost.
 *
 * Elements start visible in the markup and are only hidden once this script
 * confirms it can animate them. That ordering matters: if the script fails to
 * load, or the browser is old, the content is still on screen rather than
 * permanently invisible.
 *
 * IMPORTANT — what must NOT be revealed individually:
 *
 * Some grids in this theme draw their dividing lines the cheap way: a 1px `gap`
 * with the container's background showing through. `.bx-why` is one of them.
 * Animating those CELLS individually makes them transparent and shifts them
 * 14px, so the container's peach background shows through as thick bands until
 * every cell has landed — very visible on mobile, where the grid is a single
 * column and each cell animates in turn.
 *
 * The fix is to reveal the CONTAINER as one element, not its cells. Anything
 * that paints a background behind its children belongs in CONTAINER_TARGETS.
 */
(() => {
  const REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)');
  if (REDUCED.matches) return;
  if (!('IntersectionObserver' in window)) return;

  // Grids whose container paints the divider lines — reveal as a whole.
  const CONTAINER_TARGETS = ['.bx-why'];

  // Everything else can stagger safely, because each item paints its own
  // background and sits on the page, not on a coloured container.
  const ITEM_TARGETS = [
    '.bx-section .bx-head',
    '.bx-section .bx-hero__copy > *',
    '.bx-section .bx-hero__art',
    '.bx-ways > *',
    '.bx-cat',
    '.bx-quote',
    '.bx-faq',
    '.bx-news__copy',
    '.bx-news__side',
    '.bx-ft__brand',
    '.bx-ft__col',
    '.shopify-section product-card',
  ];

  const SELECTOR = CONTAINER_TARGETS.concat(ITEM_TARGETS).join(',');

  const start = () => {
    const nodes = Array.from(document.querySelectorAll(SELECTOR)).filter(
      (n) => !n.dataset.bxReveal
    );
    if (!nodes.length) return;

    // Mark first, so the CSS only hides things now that we know we can animate.
    document.documentElement.classList.add('bx-reveal-ready');

    const io = new IntersectionObserver(
      (entries, observer) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const el = entry.target;

          // Stagger siblings a little so a grid arrives as a wave, not a slab.
          // Containers never stagger — there is only one of them.
          if (!el.dataset.bxSolo) {
            const siblings = el.parentElement ? Array.from(el.parentElement.children) : [el];
            const index = Math.min(siblings.indexOf(el), 5);
            el.style.transitionDelay = `${index * 70}ms`;
          }

          el.dataset.bxReveal = 'in';
          observer.unobserve(el);
        });
      },
      { rootMargin: '0px 0px -12% 0px', threshold: 0.06 }
    );

    nodes.forEach((n) => {
      if (n.matches(CONTAINER_TARGETS.join(','))) n.dataset.bxSolo = '1';
      n.dataset.bxReveal = 'out';
      io.observe(n);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }

  // Sections re-render on theme-editor edits and on section hydration.
  document.addEventListener('shopify:section:load', start);
})();
